import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * The width at which the workspace stops being a desktop shell: the navigation rail
 * becomes a drawer, the top bar collapses to one row, and the tab bar appears at the
 * foot of the screen. It matches the `900px` breakpoint the stylesheet uses, so the
 * markup and the layout never disagree about which shell is on screen.
 */
export const mobileShellQuery = '(max-width: 900px)';

/** Coarse pointers get larger hit areas and no hover-only affordances. */
export const touchPointerQuery = '(pointer: coarse)';

/**
 * Subscribes to a media query. Rendering is driven by the match rather than by a
 * window-resize listener, so a rotation or a split-screen resize re-renders once.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window.matchMedia !== 'function') return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', onStoreChange);
      return () => list.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false),
    () => false,
  );
}

/** True while the viewport is narrow enough to need the phone shell. */
export function useIsMobileShell(): boolean {
  return useMediaQuery(mobileShellQuery);
}

/**
 * Holds the page still behind an open drawer or sheet. Without it a phone scrolls the
 * page under the overlay and the reader loses their place when the overlay closes.
 */
export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return undefined;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [locked]);
}

/** Runs `onDismiss` when Escape is pressed while `active`, for overlays that own the screen. */
export function useEscapeKey(active: boolean, onDismiss: () => void): void {
  useEffect(() => {
    if (!active) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, onDismiss]);
}
